// src/components/main/produzione/assegnazioniGantt/AssegnazioniGanttPage.tsx

import React, {useEffect, useState, useMemo} from "react";
import {useSelector} from "react-redux";
import {DateTime} from "luxon";

import store, {RootState, useAppDispatch} from "../../../../../../../redux/store";
import {
    fetchAssegnazioniGantt,
} from "./assegnazioniGanttSlice";
import {deleteAssegnazione, fetchOrdiniLavoro, saveAssegnazione} from "../../../../ordiniLavoro/ordiniLavoroSlice";
import {AssegnazioniGanttList} from "./assegnazioniGanttTypes";
import {fetchGiorniLavorativi, Macchina, resetGiorniLavorativi, Tecnica} from "components/anagrafiche/anagraficheSlice";
import {ErrorsStack, ErrorStatusTypes} from "components/common/errorsDeclarations";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import {DesktopDatePicker} from "@mui/x-date-pickers/DesktopDatePicker";
import {useTheme} from "@mui/material/styles";

import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import AssegnazioniGanttTimeline from "./AssegnazioniGanttTimeline";
import GanttOrdiniLavoroCard from "./ordiniLavoro/GanttOrdiniLavoroCard";
import GanttDragLayer from "./ordiniLavoro/GanttDragLayer";
import {
    FiltriGantt,
    FiltriOrdiniLavoro,
    resetFiltriGantt,
    setFiltriGantt
} from "../../../../../../userPreference/userPreferenceSlice";
import {convertToDateTime} from "../../../../../../common/dateUtils/convertToDateTime";
import IconButton from "@mui/material/IconButton";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import Tooltip from "@mui/material/Tooltip";
import {Assegnazione, OrdineLavoro} from "../../../ordiniLavoroTypes";
import ConfermaAnnulla from "../../../../../../common/ConfermaAnnulla";

// ============================================
// CONSTANTS
// ============================================

const OPZIONI_SETTIMANE = [
    {value: 1, label: "1 settimana"},
    {value: 2, label: "2 settimane"},
    {value: 3, label: "3 settimane"},
    {value: 4, label: "4 settimane"},
    {value: 6, label: "6 settimane"},
] as const;

type NumeroSettimane = 1 | 2 | 3 | 4 | 6;

// ============================================
// COMPONENT
// ============================================

const AssegnazioniGanttPage = () => {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    // ============================================
    // STATE LOCALE
    // ============================================

    const filtri = useSelector<RootState, FiltriGantt>(
        (state) => state.userPreferenceState.filtriGantt || {
            data_da: DateTime.now().startOf("week").toFormat('yyyy-mm-dd'),
            numeroSettimane: 2,
            tecnica_id: []
        }
    );
    const [dataDa, setDataDa] = useState<DateTime>(
        convertToDateTime(filtri.data_da) ||
        DateTime.now().startOf("week")
    );
    const [numeroSettimane, setNumeroSettimane] = useState<NumeroSettimane>(filtri.numeroSettimane || 2);
    const [tecnicheSelezionate, setTecnicheSelezionate] = useState<number[]>(filtri.tecnica_id || []);

    const [ordineLavoroSelezionatoId, setOrdineLavoroSelezionatoId] = useState<number | undefined>();

    // ============================================
    // SELECTORS
    // ============================================

    const assegnazioniList = useSelector<RootState, AssegnazioniGanttList>(
        (state) => state.assegnazioniGanttState.assegnazioniList
    );

    const macchine = useSelector<RootState, Macchina[]>(
        (state) => state.anagraficheState?.macchine?.results || []
    );

    const tecniche = useSelector<RootState, Tecnica[]>(
        (state) => state.anagraficheState?.tecniche?.results || []
    );

    // ============================================
    // COMPUTED
    // ============================================

    // Data fine calcolata
    const dataA = useMemo(
        () => dataDa.plus({weeks: numeroSettimane}),
        [dataDa, numeroSettimane]
    );

    // Filtra macchine per tecniche selezionate
    const macchineFiltrate = useMemo(() => {
        if (tecnicheSelezionate.length === 0) {
            return macchine;
        }

        return macchine.filter((macchina) =>
            macchina.tecniche?.some((tecnica) => tecnicheSelezionate.includes(tecnica)
            ))

    }, [macchine, tecnicheSelezionate,]);

    // ============================================
    // EFFECTS
    // ============================================

    useEffect(() => {
        handleRefresh();
    }, [dataDa, tecnicheSelezionate, numeroSettimane]);

    const handleResetFiltri = () => {
        // setStagioneSelezionata(undefined);
        setTecnicheSelezionate([]);
        setDataDa(DateTime.now().startOf("week"));
        setNumeroSettimane(2);
        dispatch(resetFiltriGantt());

        const filtriDefault: FiltriOrdiniLavoro = {
            numero_record: 20,
            page: 1,
            stato: ['CONF', 'ARR'],
            ordering: 'data_consegna_prevista',
        };
        handleRefresh()
    };
    // ============================================
    // HANDLERS
    // ============================================

    const handleRefresh = () => {
        const dataDaStr = dataDa.toFormat("yyyy-MM-dd");
        const dataAStr = dataA.toFormat("yyyy-MM-dd");

        const nuoviFiltri: FiltriGantt = {
            data_da: dataDaStr,
            numeroSettimane: numeroSettimane,
            tecnica_id: tecnicheSelezionate
        }
        dispatch(setFiltriGantt(nuoviFiltri))

        // Giorni lavorativi per il Gantt
        dispatch(resetGiorniLavorativi());
        dispatch(fetchGiorniLavorativi({ data_da: dataDaStr, data_a: dataAStr }));

        dispatch(
            fetchAssegnazioniGantt({
                data_da: dataDa.toFormat("yyyy-MM-dd"),
                data_a: dataA.toFormat("yyyy-MM-dd"),
                tecniche: tecnicheSelezionate.length > 0 ? tecnicheSelezionate : undefined,
            })
        );

        // // Fetch ordini lavoro (stesso comportamento del child)
        // dispatch(
        //     fetchOrdiniLavoro({
        //         data_da: dataDaStr,
        //         data_a: dataAStr,
        //         tecnica_id: tecnicheSelezionate.length > 0 ? tecnicheSelezionate : undefined,
        //         numero_record: 100,
        //     })
        // );
    };

    const handlerCreaAssegnazioneOrdineLavoro = async (
        ordineLavoroId: number,
        macchinaId: number,
        data: string) => {
        const nouvaAssegnazione: Assegnazione = {
            macchina: macchinaId,
            ordine_di_lavoro: ordineLavoroId,
            data_da: data
        }
        const result = await dispatch(saveAssegnazione({
            ordineLavoroId: ordineLavoroId,
            assegnazione: nouvaAssegnazione
        }))
        if (result.meta.requestStatus === 'fulfilled') {
            handleRefresh()
        }
    }

    const handleDataDaChange = (newValue: DateTime | null) => {
        if (newValue) {
            setDataDa(newValue.startOf("week"));
        }
    };

    const handleNumeroSettimaneChange = (event: SelectChangeEvent<number>) => {
        setNumeroSettimane(event.target.value as NumeroSettimane);
    };

    const handleAssegnazioneClick = (ordineLavoroId: number) => {
        setOrdineLavoroSelezionatoId(
            ordineLavoroSelezionatoId === ordineLavoroId ? undefined : ordineLavoroId
        );
    };

    const handleOrdineLavoroClick = (ordineLavoroId: number) => {
        setOrdineLavoroSelezionatoId(
            ordineLavoroSelezionatoId === ordineLavoroId ? undefined : ordineLavoroId
        );
    };

    const handleAssegnazioneDrop = async (
            assegnazioneId: number,
            nuovaMacchinaId: number,
            nuovaDataDa: string,
            nuovaDataA: string
        ) => {
            const state = store.getState();
            const assegnazioneToSave = state.assegnazioniGanttState.assegnazioniList.results
                                            .find(a => a.id === assegnazioneId);
            if (assegnazioneToSave) {
                const assegnazioneResized: Assegnazione = {
                    ...assegnazioneToSave,
                    macchina: nuovaMacchinaId,
                    data_a: nuovaDataA ? nuovaDataA : assegnazioneToSave?.data_a,
                    data_da: nuovaDataDa ? nuovaDataDa : assegnazioneToSave?.data_da,
                }
                console.log('------------',assegnazioneResized)
                const result = await dispatch(saveAssegnazione({
                    ordineLavoroId: assegnazioneToSave.ordine_di_lavoro!,
                    assegnazione: assegnazioneResized,
                    azione: 'spostamento'
                }))
                if (result.meta.requestStatus === 'fulfilled') {
                    handleRefresh()
                }
            }
        }
    ;

    const handleAssegnazioneResize = async (
        assegnazioneId: number,
        nuovaDataDa: string | null,
        nuovaDataA: string | null
    ) => {
        console.log("=== PAGE: RESIZE ===", {
            assegnazioneId,
            nuovaDataDa,
            nuovaDataA,
        });
        // Accedi allo stato direttamente dallo store
        const state = store.getState();
        const assegnazioneToSave = state.assegnazioniGanttState.assegnazioniList.results
                                        .find(a => a.id === assegnazioneId);

        if (assegnazioneToSave) {
            const assegnazioneResized: Assegnazione = {
                ...assegnazioneToSave,
                data_a: nuovaDataA ? nuovaDataA : assegnazioneToSave?.data_a,
                data_da: nuovaDataDa ? nuovaDataDa : assegnazioneToSave?.data_da,
            }

            const result = await dispatch(saveAssegnazione({
                ordineLavoroId: assegnazioneToSave.ordine_di_lavoro!,
                assegnazione: assegnazioneResized
            }))
            if (result.meta.requestStatus === 'fulfilled') {
                handleRefresh()
            }
        }
    };

    // Gestine Cancellazione di una assegnazione

    const [openConferma, setOpenConferma] = useState(false);
    const [assegnazioneToDelete, setAssegnazioneToDelete] = useState<Assegnazione|undefined>(undefined);
    const handleConfermaClose = () => setOpenConferma(false);

    const onAssegnazioneDelete =  (
        assegnazioneId: number,
    ) => {
        console.log("=== onAssegnazioneDelete ===", {
            assegnazioneId,
        });
        setOpenConferma(true)
        // Accedi allo stato direttamente dallo store
        const state = store.getState();
        const assegnazioneDaCancellare = state.assegnazioniGanttState.assegnazioniList.results
                                        .find(a => a.id === assegnazioneId);
        setAssegnazioneToDelete(assegnazioneDaCancellare);
        if (assegnazioneToDelete) {
            setOpenConferma(true)
        }

    };


    const handleOrdineLavoroDrop = (
        ordineLavoroId: number,
        macchinaId: number,
        data: string
    ) => {
        console.log("=== PAGE: NUOVO SCHEDULING ORDINE LAVORO ===", {
            ordineLavoroId,
            macchinaId,
            data,
        });
        handlerCreaAssegnazioneOrdineLavoro(ordineLavoroId,
            macchinaId,
            data,)
        // TODO: dispatch per creare nuova assegnazione
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <Box sx={{p: 2}}>
            <GanttDragLayer/>
            {/* Header */}
            <Card sx={{mb: 2}}>
                <CardContent>
                    <Grid container spacing={2} sx={{alignItems: "center"}}>
                        {/* Titolo */}
                        <Grid size={{xs: 12, md: 2}}>
                            <Stack direction="row" spacing={1} sx={{alignItems: "center"}}>
                                <CalendarMonthIcon/>
                                <Typography variant="h6">Gantt Assegnazioni</Typography>
                            </Stack>
                        </Grid>

                        {/* Data inizio */}
                        <Grid size={{xs: 6, md: 2}}>
                            <DesktopDatePicker
                                label="Data Inizio"
                                value={dataDa}
                                onChange={handleDataDaChange}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        fullWidth: true,
                                    },
                                }}
                            />
                        </Grid>

                        {/* Numero settimane */}
                        <Grid size={{xs: 6, md: 2}}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Durata</InputLabel>
                                <Select
                                    value={numeroSettimane}
                                    label="Durata"
                                    onChange={handleNumeroSettimaneChange}
                                >
                                    {OPZIONI_SETTIMANE.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Filtro tecniche */}
                        <Grid size={{xs: 12, md: 4}}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={tecniche}
                                getOptionLabel={(option) => option.nome || ""}
                                value={tecniche.filter((t) =>
                                    tecnicheSelezionate.includes(t.id)
                                )}
                                onChange={(_, newValue) => {
                                    setTecnicheSelezionate(newValue.map((t) => t.id));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Filtra per Tecniche"
                                        placeholder="Tutte"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Bottone aggiorna */}
                        <Grid size={{xs: 12, md: 2}}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon/>}
                                onClick={handleRefresh}
                                // fullWidth
                            >
                                Aggiorna
                            </Button>
                            <Tooltip title="Reset filtri">
                                <IconButton onClick={handleResetFiltri} color="primary">
                                    <FilterAltOffIcon/>
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>

                    {/* Info periodo */}
                    <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: "block"}}>
                        Periodo: {dataDa.toFormat("dd/MM/yyyy")} - {dataA.toFormat("dd/MM/yyyy")} |
                        Macchine: {macchineFiltrate.length} |
                        Assegnazioni: {assegnazioniList.results.length}
                    </Typography>
                </CardContent>
            </Card>

            {/* Timeline + Spalla OdL */}
            <Grid container spacing={2}>
                <Grid size={{xs: 10}}>
                    <AssegnazioniGanttTimeline
                        macchine={macchineFiltrate}
                        dataDa={dataDa}
                        numeroSettimane={numeroSettimane}
                        ordineLavoroSelezionatoId={ordineLavoroSelezionatoId}
                        onAssegnazioneClick={handleAssegnazioneClick}
                        onAssegnazioneDrop={handleAssegnazioneDrop}
                        onAssegnazioneResize={handleAssegnazioneResize}
                        onAssegnazioneDelete={onAssegnazioneDelete}
                        onOrdineLavoroDrop={handleOrdineLavoroDrop}
                    />
                </Grid>
                <Grid size={{xs: 2}}>
                    <GanttOrdiniLavoroCard
                        dataDa={dataDa}
                        numeroSettimane={numeroSettimane}
                        tecnicheSelezionate={tecnicheSelezionate}
                        ordineLavoroSelezionatoId={ordineLavoroSelezionatoId}
                        onOrdineLavoroClick={handleOrdineLavoroClick}
                    />
                </Grid>
            </Grid>

            {/* Loading */}
            <Backdrop
                open={assegnazioniList.errorsStack.status === ErrorStatusTypes.PENDING}
                sx={{color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1}}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>

            <ConfermaAnnulla
                handleAnnulla={handleConfermaClose}
                handleConferma={async () =>  {
                    if (assegnazioneToDelete) {

                        const result = await dispatch(deleteAssegnazione(
                            {ordineLavoroId: assegnazioneToDelete?.ordine_di_lavoro!, assegnazioneId: assegnazioneToDelete.id!}
                        ))
                        if (result.meta.requestStatus === 'fulfilled') {
                            handleRefresh()
                        }
                    }
                    handleConfermaClose();
                }}
                domanda={
                    `Sicuro di voler cancellare l'assegnazione per l'OdL ${assegnazioneToDelete?.ordine_di_lavoro_display} sulla macchina ${assegnazioneToDelete?.macchina_display} previsto dal ${convertToDateTime(assegnazioneToDelete?.data_da)?.toFormat('DDD')}`
                }
                open={openConferma}
            />
        </Box>
    );
};

export default AssegnazioniGanttPage;